import { NextRouter, useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useSearchStore } from '../store/search';
import { getHomeSearchQuery, getHomeSearchUrl } from '../functions/homeSearchRoute';

const SearchRouterContext = createContext<NextRouter | null>(null);

/** Shared search components use a scoped route only while embedded in home. */
export const useSearchRouter = () => {
  const router = useRouter();
  return useContext(SearchRouterContext) ?? router;
};

const SHELL_KEYS = ['application', 'download_source'];

/**
 * Home keeps its embedded search state (city, page) in memory: the home URL never changes for it.
 * Changes that belong to the search page (filters, sort, text) open /s.
 */
export const HomeSearchProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const city = useSearchStore(state => state.city.en_slug);
  const [homeQuery, setHomeQuery] = useState<ParsedUrlQuery>(() =>
    Object.fromEntries(Object.entries(router.query).filter(([key]) => !SHELL_KEYS.includes(key))),
  );
  const value = useMemo<NextRouter>(() => {
    const query = getHomeSearchQuery(homeQuery, city);
    const params = query.params as string[];
    const shellQuery = Object.fromEntries(Object.entries(router.query).filter(([key]) => SHELL_KEYS.includes(key)));
    const navigate = (method: 'push' | 'replace'): NextRouter['push'] => (url, as, options) => {
      const destination = getHomeSearchUrl(url, {}, { ...homeQuery, city: params[0], category: params[1] });
      if (destination === url) return router[method](url, as, options);
      if (typeof destination === 'object' && destination.pathname === '/apphome') {
        setHomeQuery(Object.fromEntries(Object.entries(destination.query ?? {}).filter(([, v]) => v !== undefined)) as ParsedUrlQuery);
        return Promise.resolve(true);
      }
      return router.push(
        typeof destination === 'object' ? { ...destination, query: { ...(destination.query as object), ...shellQuery } } : destination,
        undefined,
        { scroll: true },
      );
    };
    const homeKey = Object.entries(query)
      .map(([key, v]) => `${key}=${v}`)
      .sort()
      .join('&');
    return { ...router, query, asPath: `${router.pathname}|${homeKey}`, push: navigate('push'), replace: navigate('replace') };
  }, [router, homeQuery, city]);
  return <SearchRouterContext.Provider value={value}>{children}</SearchRouterContext.Provider>;
};
